'use client'

import { motion } from 'framer-motion'

const PREVIEW_KEYS = new Set([
  'richtext',
  'card',
  'tabs',
  'hero',
  'callout',
  'quote',
  'code',
  'media',
  'insight',
  'summary',
  'question',
  'reference',
  'timeline',
  'warning',
  'rule',
  'grid',
  'steps',
  'daily',
  'lesson',
  'checklist',
  'badge',
  'default',
])

/**
 * Mini animated “app icon” preview for launchpad tiles — CSS only.
 */
export function BlockLaunchpadPreview({ variant = 'default', hue = 220 }) {
  const key = PREVIEW_KEYS.has(variant) ? variant : 'default'

  return (
    <div
      className="nb-launchpad-preview"
      style={{
        '--lp-hue': String(hue),
        '--lp-sat': '72%',
      }}
      data-preview={key}
      aria-hidden
    >
      <motion.div
        className="nb-launchpad-preview__inner"
        initial={{ opacity: 0, scale: 0.92 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="nb-launchpad-preview__scene">
          {key === 'richtext' && <PreviewRichtext />}
          {key === 'card' && <PreviewCard />}
          {key === 'tabs' && <PreviewTabs />}
          {key === 'hero' && <PreviewHero />}
          {key === 'callout' && <PreviewCallout />}
          {key === 'quote' && <PreviewQuote />}
          {key === 'code' && <PreviewCode />}
          {key === 'media' && <PreviewMedia />}
          {key === 'insight' && <PreviewInsight />}
          {key === 'summary' && <PreviewSummary />}
          {key === 'question' && <PreviewQuestion />}
          {key === 'reference' && <PreviewReference />}
          {key === 'timeline' && <PreviewTimeline />}
          {key === 'warning' && <PreviewWarning />}
          {key === 'rule' && <PreviewRule />}
          {key === 'grid' && <PreviewGrid />}
          {key === 'steps' && <PreviewSteps />}
          {key === 'daily' && <PreviewDaily />}
          {key === 'lesson' && <PreviewLesson />}
          {key === 'checklist' && <PreviewChecklist />}
          {key === 'badge' && <PreviewBadge />}
          {key === 'default' && <PreviewDefault />}
        </div>
        <div className="nb-launchpad-preview__shine" />
      </motion.div>
    </div>
  )
}

function Line({ w = '100%', h = 3, o = 0.5 }) {
  return (
    <span
      className="nb-lp-line"
      style={{ width: w, height: h, opacity: o }}
    />
  )
}

function PreviewRichtext() {
  return (
    <>
      <Line w="72%" />
      <Line w="88%" />
      <Line w="64%" o={0.35} />
      <span className="nb-lp-chip" />
    </>
  )
}

function PreviewCard() {
  return (
    <>
      <span className="nb-lp-pill" />
      <Line w="78%" h={4} />
      <Line w="92%" />
      <Line w="55%" o={0.4} />
    </>
  )
}

function PreviewTabs() {
  return (
    <>
      <span className="nb-lp-tab active" />
      <span className="nb-lp-tab" />
      <span className="nb-lp-tab" />
      <Line w="100%" h={5} o={0.45} />
    </>
  )
}

function PreviewHero() {
  return (
    <>
      <Line w="40%" h={4} o={0.7} />
      <Line w="95%" h={6} o={0.85} />
      <Line w="70%" o={0.4} />
    </>
  )
}

function PreviewCallout() {
  return (
    <span className="nb-lp-callout">
      <Line w="80%" />
      <Line w="60%" o={0.45} />
    </span>
  )
}

function PreviewQuote() {
  return (
    <>
      <span className="nb-lp-quote-mark">“</span>
      <Line w="85%" />
      <Line w="50%" o={0.4} />
    </>
  )
}

function PreviewCode() {
  return (
    <>
      <Line w="55%" o={0.55} />
      <Line w="78%" o={0.4} />
      <Line w="48%" o={0.35} />
      <span className="nb-lp-cursor" />
    </>
  )
}

function PreviewMedia() {
  return (
    <span className="nb-lp-media">
      <span className="nb-lp-play" />
    </span>
  )
}

function PreviewInsight() {
  return (
    <>
      <span className="nb-lp-glow-dot" />
      <Line w="82%" />
      <Line w="68%" o={0.4} />
    </>
  )
}

function PreviewSummary() {
  return (
    <>
      <Line w="90%" h={4} />
      <span className="nb-lp-bullets">
        <i />
        <i />
        <i />
      </span>
    </>
  )
}

function PreviewQuestion() {
  return (
    <>
      <span className="nb-lp-q">?</span>
      <Line w="75%" />
    </>
  )
}

function PreviewReference() {
  return (
    <>
      <Line w="35%" h={3} o={0.55} />
      <Line w="88%" />
      <Line w="42%" o={0.35} />
    </>
  )
}

function PreviewTimeline() {
  return (
    <span className="nb-lp-timeline">
      <i />
      <i />
      <i />
    </span>
  )
}

function PreviewWarning() {
  return (
    <span className="nb-lp-warning">
      <span className="nb-lp-warn-icon">!</span>
      <Line w="70%" />
    </span>
  )
}

function PreviewRule() {
  return (
    <>
      <span className="nb-lp-rule-bar" />
      <Line w="80%" />
      <Line w="65%" o={0.4} />
    </>
  )
}

function PreviewGrid() {
  return (
    <span className="nb-lp-grid">
      <i />
      <i />
      <i />
      <i />
    </span>
  )
}

function PreviewSteps() {
  return (
    <>
      <span className="nb-lp-step">
        <i>1</i>
        <Line w="70%" />
      </span>
      <span className="nb-lp-step">
        <i>2</i>
        <Line w="55%" o={0.4} />
      </span>
    </>
  )
}

function PreviewDaily() {
  return (
    <span className="nb-lp-daily">
      <i />
      <i />
      <i />
    </span>
  )
}

function PreviewLesson() {
  return (
    <span className="nb-lp-lesson">
      {Array.from({ length: 6 }).map((_, i) => (
        <i key={i} />
      ))}
    </span>
  )
}

function PreviewChecklist() {
  return (
    <>
      <span className="nb-lp-check-row">
        <i />
        <Line w="72%" />
      </span>
      <span className="nb-lp-check-row">
        <i />
        <Line w="58%" o={0.4} />
      </span>
    </>
  )
}

function PreviewBadge() {
  return (
    <>
      <span className="nb-lp-badge-pill" />
      <Line w="60%" o={0.45} />
    </>
  )
}

function PreviewDefault() {
  return (
    <>
      <Line w="80%" />
      <Line w="55%" o={0.4} />
    </>
  )
}
